/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-async-promise-executor */
/**
 * @copyright (C) 2023, Rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of Rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by Rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

import { EventHandler } from "@rfice/eventhandler";
import { LocalData, SessionData } from "../storage";

type ConfigureItemOptions = Partial<{
	useCrypto: boolean;
	useUniqueID: boolean;
	storageType: "local" | "session" | "cache";
	cache: boolean;
}>;

const ConfigureItemDefaultOptions: ConfigureItemOptions = {
	useCrypto: false,
	useUniqueID: false,
	storageType: "local",
	cache: false
};

export default class ConfigureItem<T = any> {
	private _options: ConfigureItemOptions = ConfigureItemDefaultOptions;
	private _cache: T | null = null;
	public static Keys: string[] = [];

	constructor(
		public name: string,
		public readonly defaultValue: T,
		public readonly options?: ConfigureItemOptions
	) {
		this._options = { ...ConfigureItemDefaultOptions, ...(options ? options : {}) };

		window.addEventListener("storage", (e) => {
			const storageArea = this._options.storageType === "local" ? localStorage : sessionStorage;
			if (e.storageArea === storageArea && e.key === name) {
				this.fireEvent({
					key: e.key,
					newValue: e.newValue,
					oldValue: e.oldValue
				});
			}
		});

		if (this._options?.cache) {
			this._cache = this._read;
			this._remove();
			window.addEventListener("beforeunload", () => {
				if (this._options?.cache) {
					this._write = this._cache;
				}
			});
		}
	}

	private fireEvent(event: { key: string; newValue: unknown; oldValue: unknown }) {
		EventHandler.fire("$Configure::Changed$", event);
	}

	public get defaultOptions() {
		return ConfigureItemDefaultOptions;
	}

	public get configureName() {
		// if (this._options.useUniqueID!) {
		// 	const oldData = this.storage.get(`${Properties.oldUniqueID}::${this.name}`, null);
		// 	if (oldData) {
		// 		this.storage.set(`${Properties.uniqueID}::${this.name}`, oldData);
		// 		this.storage.remove(`${Properties.oldUniqueID}::${this.name}`);
		// 	}
		// 	return `${Properties.uniqueID}::${this.name}`;
		// }
		return this.name;
	}

	private get _read() {
		return this.storage.get(this.configureName, this.defaultValue, this._options.useCrypto) as T;
	}

	public get read() {
		if (this._options?.cache) {
			return this._cache as T;
		}
		return this._read;
	}

	public get read_number() {
		return parseInt(this.read as string);
	}

	private set _write(value: T | null) {
		if (value !== null) {
			this.storage.set(this.configureName, value, this._options.useCrypto);
		} else {
			this.remove();
		}
	}

	public set write(value: T | null) {
		const oldValue = this.read;
		if (this._options?.cache) {
			this._cache = value;
		} else {
			this._write = value;
		}
		this.fireEvent({
			key: this.name,
			newValue: value,
			oldValue
		});
	}

	public set write_number(value: number | null) {
		this.write = !value ? (`${value}` as T) : null;
	}

	public get storage() {
		if (this._options.storageType === "local") {
			return LocalData;
		} else if (this._options.storageType === "cache") {
			return LocalData;
		}
		return SessionData;
	}

	private _remove() {
		this.storage.remove(this.configureName);
	}
	public remove() {
		this._remove();
	}
}
